/**
 * The bug icon in the app bar and the dialog it opens: everything this page sees,
 * as one copyable JSON blob (`lib/debugSnapshot.ts`). The UI is a projection of a
 * few live documents plus the derivations the pages make over them, so when it
 * shows something confusing the question is always "what were the inputs, and
 * what did the page derive from them?" — this answers both at once, and the
 * operator pastes the answer into a chat or a bug report instead of describing a
 * screenshot.
 *
 * The dialog subscribes to the same documents the pages do (the SDK multiplexes
 * identical listeners, so this costs nothing extra) and re-derives every few
 * seconds so clock-derived fields — heartbeat age, in-flight durations — stay
 * honest while it is open. It is rendered only while open so the subscriptions do
 * not outlive it.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { SDK_VERSION } from 'firebase/app';
import { firebaseConfig } from '../firebase';
import { useOperator } from '../auth';
import { useDefaultReleases, useHost, useNow, usePrepareReleases, useRecentInstructions, useUnreleased } from '../hooks';
import { buildDebugSnapshot, serializeSnapshot } from '../lib/debugSnapshot';

const IS_EMULATOR = import.meta.env.VITE_USE_EMULATOR === 'true';

/** Re-derive this often while open; fine-grained enough for "is the heartbeat stale?". */
const SNAPSHOT_TICK_MS = 5_000;

/** How long the Copy button says "Copied" before reverting. */
const COPIED_FEEDBACK_MS = 2_000;

export function DebugButton() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Tooltip title="Show the state this page sees, as JSON to copy into a bug report or a chat">
                <IconButton size="small" onClick={() => setOpen(true)} aria-label="Debug: show page state">
                    <BugReportOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {open && <DebugDialog onClose={() => setOpen(false)} />}
        </>
    );
}

function DebugDialog({ onClose }: { onClose: () => void }) {
    const now = useNow(SNAPSHOT_TICK_MS);
    const location = useLocation();
    const operator = useOperator();
    const prepareHost = useHost('prepare');
    const releaseHost = useHost('release');
    const prepareReleases = usePrepareReleases();
    const deployReleases = useDefaultReleases();
    const unreleased = useUnreleased();
    const prepareInstructions = useRecentInstructions('prepare');
    const releaseInstructions = useRecentInstructions('release');

    // The subscriptions go over whole, loading flag included, for the sources whose
    // derivations turn on it: a dialog opened during the first seconds would
    // otherwise read an unarrived host as a stopped one and report the sleeping
    // panel for a page that was showing its spinner.
    const text = serializeSnapshot(
        buildDebugSnapshot({
            now,
            route: location.pathname,
            env: {
                projectId: firebaseConfig.projectId,
                emulator: IS_EMULATOR,
                mode: import.meta.env.MODE,
                firebaseSdk: SDK_VERSION,
                userAgent: navigator.userAgent,
            },
            operator: { uid: operator.uid, email: operator.email },
            hosts: { prepare: prepareHost, release: releaseHost },
            prepareReleases,
            deployReleases,
            unreleased: unreleased.value,
            instructions: { prepare: prepareInstructions.value, release: releaseInstructions.value },
        }),
    );

    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopyState('copied');
        } catch {
            // Clipboard access needs a secure context and a user gesture; if it is
            // refused the text is still selectable below, so say so rather than
            // failing silently.
            setCopyState('failed');
        }
        setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);
    };

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>What this page sees</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    The live documents this page subscribes to, beside what it derives from them: the phase and the
                    facts behind it, the last unreleased-changes check and what the idle page concluded from it, each
                    host&apos;s presentation from its heartbeat, in-flight and latest-per-kind instructions, and which
                    actions the legality table allows right now. Timestamps are ISO; log tails are trimmed. It
                    refreshes while open.
                </Typography>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 1.5,
                        fontSize: 12,
                        lineHeight: 1.4,
                        bgcolor: 'grey.50',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: '60vh',
                        overflow: 'auto',
                        userSelect: 'text',
                    }}
                >
                    {text}
                </Box>
            </DialogContent>
            <DialogActions>
                <Typography variant="caption" color={copyState === 'failed' ? 'error' : 'text.secondary'} sx={{ mr: 'auto', ml: 1 }}>
                    {copyState === 'copied' && 'Copied to clipboard.'}
                    {copyState === 'failed' && 'Clipboard unavailable — select the text above and copy it.'}
                </Typography>
                <Button onClick={() => void copy()} variant="contained">
                    Copy JSON
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
